import styles from "./SendBox.module.scss"
import "./SendBox.scss"

import React, {
  ClipboardEvent,
  KeyboardEvent,
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"

import {
  useAccount,
  useHandleMessagesContext,
  useLayerPopupContext,
  useMessagesContext,
  useMessageSender,
  useMessageSenderUpdater,
  useSchedule,
} from "@shared/contexts"
import { useChatFileDrop, useDetectTrigger } from "@shared/hooks"
import { Message } from "@shared/hooks/useMessages"
import {fileConstructor, mentionTagging, redirectLoginUrl} from "@shared/modules"

import classNames from "classnames"

import DropGuide from "./DropGuide"
import Suggestion from "./Suggestion"
import CabinetApi from "../../apis/CabinetApi";
import PreviewImage from "./PreviewImage";

const START_INDEX = 0
const SUGGEST_COUNT = 5

/** 파일 선택창(accept)과 동일한 허용 형식 */
const ACCEPTED_IMAGE_TYPES = ["image/jpg", "image/jpeg", "image/png"]

/**
 * 텍스트 붙여넣기로 처리해야 하는지 판단한다.
 *
 * 클립보드는 한 번의 복사에 여러 형식을 함께 담는다.
 * 엑셀·파워포인트 같은 오피스 계열은 텍스트를 복사해도 선택 영역을 그린
 * image/png 스냅샷을 항상 끼워 넣기 때문에, 이미지가 들어있다는 것만으로
 * 판단하면 텍스트 붙여넣기가 전부 이미지 전송이 돼버린다.
 *
 * 크롬은 이 스냅샷을 합성 파일로 노출하면서 types 에 "Files" 까지 넣으므로
 * 파일 여부로는 오피스 복사와 실제 파일 복사를 가릴 수 없다.
 *
 * 캡처 이미지나 브라우저에서 우클릭으로 복사한 이미지, 탐색기에서 복사한
 * 이미지 파일에는 텍스트가 없으므로 그대로 이미지로 처리된다.
 */
const isTextPaste = (clipboard: DataTransfer) =>
  Boolean(clipboard.getData("text/plain"))

interface Props {
  isUser?: boolean
  macroMessage?: string
}

function SendBox({ isUser = true, macroMessage }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)

  const { account } = useAccount()
  const { schedule } = useSchedule()
  const {
    open,
    chat,
    close,
    managerFont,
    alert: { handleAlert, contents },
  } = useLayerPopupContext()
  const { sentViewer } = useMessagesContext()
  const { send } = useHandleMessagesContext()

  const message = useMessageSender()
  const { handleMessage } = useMessageSenderUpdater()
  const detectTrigger = useDetectTrigger({ inputRef })

  const [selectedEmoji, setSelectedEmoji] = useState<string>("")
  const [isTrigger, setIsTrigger] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState<string>("")

  const isAnonymous = account?.viewer?.role === "ANONYMOUS"
  const isEmoticon = schedule?.isEmoticon

  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File>();
  const [url, setUrl] = useState<any>("");
  const [isUploading, setIsUploading] = useState<boolean>(false);

  /** 미리보기를 닫고, 빼앗아 두었던 포커스를 입력창으로 되돌린다 */
  const initFile = useCallback(() => {
    setFile(undefined);
    setUrl("");
    inputRef.current?.focus();

    if (!fileRef.current) return;

    fileRef.current.value = "";
  }, []);

  /**
   * 이미지를 '전송 전 미리보기' 상태로 만든다.
   * 파일 선택 / 드래그&드롭 / 붙여넣기가 모두 이 경로를 쓴다.
   */
  const handleSelectFile = useCallback(
    (selected: File) => {
      if (!ACCEPTED_IMAGE_TYPES.includes(selected.type)) {
        handleAlert({ contents: "jpg, png 이미지만 등록할 수 있습니다." })
        open("ALERT")

        return
      }

      const fileReader = new FileReader()
      const urlReader = new FileReader()

      fileReader.readAsArrayBuffer(selected)
      fileReader.onloadend = () => {
        const blob = fileReader.result as BlobPart
        // 붙여넣은 이미지는 파일명이 없을 수 있어 MIME 타입에서 확장자를 얻는다
        const extension = selected.type.split("/").pop()
        const newFileName = `${new Date().getTime()}.${extension}`

        setFile(fileConstructor(new Blob([blob]), newFileName))
      }

      urlReader.readAsDataURL(selected)
      urlReader.onloadend = () => {
        setUrl(urlReader.result)
      }
    },
    [handleAlert, open],
  )

  const dropAreaEl = useChatFileDrop({
    anchorRef: inputRef,
    disabled: isUser || isAnonymous,
    onDropFile: handleSelectFile,
  })

  const uploadImage = useCallback(
    (file: File) => {
      const formData = new FormData();
      formData.append("file_box", "wownet");
      formData.append("file", file, file.name);
      setIsUploading(true);

      CabinetApi.post(formData)
        .then(({ data: file }) => {
          send(
            isUser
              ? ({ body: "", imageUrl: file.url } as Message)
              : ({ body: "", size: managerFont.size, imageUrl: file.url } as Message)
          );
          setIsUploading(false);
        })
        .catch((e) => {
          console.log("error = ", e);

          handleAlert({ contents: "이미지 업로드에 실패했습니다. 다시 시도해주세요" })
          open("ALERT")

          setIsUploading(false);
        });
      initFile();
    },
    [
      send,
      initFile,
      managerFont,
      handleAlert,
      isUser,
      open
      // handleOpen
    ]
  );

  const sendMessage = () => {
    // 미리보기 중에는 마우스로만 전송한다. 엔터는 아무 동작도 하지 않는다
    if (file && url) return

    if (!message.length && !selectedEmoji) {
      handleAlert({ contents: "메시지를 입력해주세요." })
      open("ALERT")

      return
    }

    if (selectedEmoji) {
      isUser
        ? !isEmoticon
          ? alert("이모티콘이 금지되어 있습니다.")
          : send({
              body: "",
              emoji: selectedEmoji,
            } as Message)
        : send({
            body: "",
            emoji: selectedEmoji,
            size: managerFont.size,
          } as Message)
      setSelectedEmoji("")
      return
    }
    isUser
      ? send({
          body: message,
        } as Message)
      : send({
          body: mentionTagging(message),
          color: managerFont.color,
          size: managerFont.size,
          isBold: managerFont.isBold,
        } as Message)

    handleMessage("")
  }

  useEffect(() => {
    if (!file) return;

    if (file.size < 2 * 1024 * 1024) return;

    handleAlert({ contents: "이미지 크기가 2MB 보다 작아야 합니다." })
    open("ALERT")
    initFile();
  }, [file, initFile, handleAlert, open]);

  /**
   * 미리보기가 열리는 동안에는 어디에도 포커스를 두지 않는다.
   * 붙여넣기 직후에는 입력창에 포커스가 남아 있어 직접 걷어내야 한다
   */
  useEffect(() => {
    if (!file || !url) return

    ;(document.activeElement as HTMLElement | null)?.blur()
  }, [file, url])

  useEffect(() => {
    inputRef.current?.focus()
  }, [chat.emoticon])

  useEffect(() => {
    if (!chat.emoticon) return

    setSelectedEmoji(chat.emoticon)
    chat.handleChat({ emoticon: "" })
    close()
    // eslint-disable-next-line
  }, [chat.emoticon])

  useEffect(() => {
    if (!macroMessage) return

    handleMessage(macroMessage)
    inputRef.current?.focus()
  }, [macroMessage, handleMessage])

  useEffect(() => {
    if (contents) {
      inputRef.current?.blur()
    }
  }, [contents])

  const handleIsTrigger = useCallback((isTrigger: boolean) => {
    setIsTrigger(isTrigger)
  }, [])

  return (
    <React.Fragment>
      {isTrigger && sentViewer && (
        <Suggestion
          inputRef={inputRef}
          searchQuery={searchQuery}
          sentViewer={sentViewer
            ?.filter((viewer) => viewer.includes(searchQuery))
            .slice(START_INDEX, SUGGEST_COUNT)}
          handleIsTrigger={handleIsTrigger}
          handleMessage={handleMessage}
        />
      )}
      <DropGuide container={dropAreaEl} />
      {url && file && (
        <PreviewImage
          file={file}
          url={url}
          uploadImage={uploadImage}
          initFile={initFile}
        />
      )}
      <div
        className="inputB"
        onClick={() => {
          if (!isAnonymous) return

          open("ANONYMOUS")
        }}
      >
        {selectedEmoji && (
          <div
            className="emoticonView"
            onKeyPress={(e: KeyboardEvent) =>
              e.key === "Enter" && sendMessage()
            }
            tabIndex={0}
            ref={(e: HTMLDivElement) => {
              e?.focus()
            }}
          >
            <a
              href="#!"
              className="btnX"
              title="닫기"
              onClick={(e: MouseEvent) => {
                e.preventDefault()
                setSelectedEmoji("")
              }}
            >
              닫기
            </a>
            <span>
              <a
                href="#!"
                onClick={(e: MouseEvent) => {
                  e.preventDefault()
                  sendMessage()
                }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/Assets/Emoticons/${selectedEmoji}.gif`}
                  alt="이모티콘 미리보기"
                />
              </a>
            </span>
          </div>
        )}
        {!isUser && (
          <div
            className={"image"}
          >
            <a
              href="#!"
              onClick={(e) => {
                if (isUser) return;

                if (!isAnonymous && !isUploading) return;
                if (!schedule?.id) return;

                e.preventDefault();
                if (isAnonymous) {
                  redirectLoginUrl(schedule.id)
                }
              }}
            >
              <label htmlFor="ex_file">이미지 업로드</label>
              <input
                type="file"
                accept="image/jpg, image/jpeg, image/png"
                id="ex_file"
                ref={fileRef}
                onChange={(e) => {
                  if (!e.target.files?.length) return;

                  handleSelectFile(e.target.files[0]);
                }}
              />
            </a>
          </div>
        )}
        <input
          name="name"
          type="text"
          className={classNames({bold: !isUser && managerFont.isBold})}
          style={isUser ? undefined : {color: managerFont.color}}
          placeholder={
            isAnonymous ? "로그인 후 이용 가능합니다." : (isUser && !schedule?.isChatEnabled ? "현재 채팅 입력을 할 수 없습니다." : "내용을 입력해주세요.")
          }
          autoComplete="off"
          disabled={isAnonymous || !!selectedEmoji || (isUser ? !schedule?.isChatEnabled : false)}
          value={message}
          onChange={(e) => handleMessage(e.target.value)}
          onKeyPress={(e: KeyboardEvent) => {
            e.key === "Enter" && !isTrigger && sendMessage()
          }}
          onPaste={(e: ClipboardEvent<HTMLInputElement>) => {
            if (isUser) return

            const clipboard = e.clipboardData

            if (!clipboard || isTextPaste(clipboard)) return

            const items = clipboard.items

            if (!items) return

            for (let i = 0; i < items.length; i += 1) {
              const item = items[i]

              if (item.kind !== "file" || !item.type.startsWith("image/")) {
                continue
              }

              const pasted = item.getAsFile()

              if (!pasted) continue

              // 이미지가 텍스트로 붙는 것을 막고 미리보기로 넘긴다
              e.preventDefault()
              handleSelectFile(pasted)

              return
            }
          }}
          ref={inputRef}
          onKeyUp={() => {
            if (isUser || !sentViewer) return

            const triggerResult = detectTrigger()

            if (triggerResult === null || triggerResult === undefined)
              return setIsTrigger(false)

            setIsTrigger(true)
            setSearchQuery(triggerResult)
          }}
        />
        {isEmoticon || !isUser ? (
          <a
            href="#!"
            className={classNames("emoticon", {
              [styles.anonymousEmoji]: isAnonymous,
            })}
            title="이모티콘"
            onClick={(e: MouseEvent) => {
              e.preventDefault()

              if (isAnonymous) return open("ANONYMOUS")
              // 사용자의 입력은 막아놓음.
              if (!schedule?.isChatEnabled && isUser) return;

              open("EMOTICON")
            }}
          >
          이모티콘
          </a>
        ) : null}
        <a
          href="#!"
          className={classNames("btn", {
            [styles.anonymousLogin]: isAnonymous,
          })}
          title="입력"
          onClick={(e: MouseEvent) => {
            e.preventDefault()

            if (isAnonymous) return open("ANONYMOUS")
            // 사용자의 입력은 막아놓음.
            if (!schedule?.isChatEnabled && isUser) return;

            sendMessage()
            setIsTrigger(false)
          }}
        >
          {isAnonymous ? "로그인" : "입력"}
        </a>
      </div>
    </React.Fragment>
  )
}

export default memo(SendBox)
